import { SQL_DATABASE_CATALOG, sqlImportSyncTargetKind } from '@/features/sql-sync';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { useAnalytics } from '@/services/analytics';
import { useSqlImportDetail } from './context';

/** The tabs that report a view. Each is its own route, so a switch changes the pathname. */
export type SqlImportTabViewEvent =
  | 'importsTabViewed'
  | 'queryTabViewed'
  | 'relationshipQueryTabViewed'
  | 'settingsTabViewed';

/**
 * Reports a tab view, matching what Ember sent before the migration (FPS-4001) so the existing
 * series continue.
 *
 * Keyed on the pathname alone: the tabs keep some UI state in the query string, and a filter or a
 * `runQuery` flag changing is the same view rather than a new one. Ember fired these on the route
 * transition, which is the behaviour this reproduces.
 */
export function useSqlImportTabViewTracking(event: SqlImportTabViewEvent): void {
  const { type, sqlImport, syncTarget } = useSqlImportDetail();
  const { track } = useAnalytics();

  usePageViewTracking(
    () => {
      const databaseType = SQL_DATABASE_CATALOG[type].name;

      if (event === 'importsTabViewed') {
        void track('sqlImports', 'importsTabViewed', {
          'Database Type': databaseType,
          'Sync Status': sqlImport.active ? 'Active' : 'Paused',
        });
        return;
      }

      void track('sqlImports', event, {
        'Database Type': databaseType,
        'Sync Target': sqlImportSyncTargetKind(syncTarget),
      });
    },
    true,
    { ignoreSearch: true },
  );
}
